import { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { MessageCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";

/**
 * SupportTicketDialog — lightweight "Report an issue" modal.
 * Submits to POST /api/support/tickets which routes the ticket to the
 * correct tenant's Franchise admin inbox.
 *
 * Props:
 *   - triggerClassName / triggerLabel: customize the launch button
 *   - bookingId (optional): if this is opened from a booking-specific screen,
 *     the ticket is attached to that booking
 *   - initialEmail (optional): prefill from auth context
 */
export default function SupportTicketDialog({
  bookingId,
  initialEmail = "",
  triggerLabel = "Report an issue",
  triggerClassName = "text-xs text-zinc-400 hover:text-white underline-offset-2 hover:underline",
  testIdPrefix = "support",
  source = "customer",
}) {
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState(initialEmail);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const reset = () => {
    setSubject("");
    setMessage("");
    setDone(false);
  };

  const submit = async () => {
    if (subject.trim().length < 3) return toast.error("Please add a short subject");
    if (message.trim().length < 5) return toast.error("Please describe the issue");
    setSubmitting(true);
    try {
      await axios.post(`${API}/support/tickets`, {
        subject: subject.trim(),
        message: message.trim(),
        customer_email: email.trim() || null,
        booking_id: bookingId || null,
        source,
      });
      setDone(true);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Couldn't send. Try again.";
      toast.error(typeof msg === "string" ? msg : "Couldn't send. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => { reset(); setOpen(true); }}
        className={triggerClassName}
        data-testid={`${testIdPrefix}-open-btn`}
      >
        <MessageCircle className="inline-block w-3 h-3 mr-1" />
        {triggerLabel}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 max-w-md" data-testid={`${testIdPrefix}-dialog`}>
          {done ? (
            <div className="text-center py-4 space-y-3">
              <div className="w-14 h-14 rounded-full bg-emerald-500/15 flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-7 h-7 text-emerald-400" />
              </div>
              <DialogHeader>
                <DialogTitle className="text-white">Message sent</DialogTitle>
                <DialogDescription>
                  We&apos;ll follow up shortly. If it&apos;s urgent, please give us a call.
                </DialogDescription>
              </DialogHeader>
              <Button
                onClick={() => setOpen(false)}
                className="bg-[#0047FF] hover:bg-[#0047FF]/90"
                data-testid={`${testIdPrefix}-close-btn`}
              >
                Close
              </Button>
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="text-white">Report an issue</DialogTitle>
                <DialogDescription>
                  Tell us what happened. Your operator sees this in their support inbox and gets back to you.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div className="space-y-1">
                  <Label className="text-xs">Subject</Label>
                  <Input
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="e.g. Driver never showed up"
                    className="bg-black border-white/10"
                    data-testid={`${testIdPrefix}-subject`}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">What happened?</Label>
                  <Textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={4}
                    placeholder="Be as specific as you can — dates, addresses, driver name if known."
                    className="bg-black border-white/10"
                    data-testid={`${testIdPrefix}-message`}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Email to reply to (optional)</Label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="bg-black border-white/10"
                    data-testid={`${testIdPrefix}-email`}
                  />
                </div>
                <Button
                  onClick={submit}
                  disabled={submitting}
                  className="w-full bg-[#0047FF] hover:bg-[#0047FF]/90"
                  data-testid={`${testIdPrefix}-submit-btn`}
                >
                  {submitting ? "Sending..." : "Send"}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
